/**
 * Security Settings Page - Comptes liés + Appareils connectés + Admin (Certificats, Breach Check)
 * ISO 27001 A.9.4.2 - Secure authentication mechanisms
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Key, ShieldAlert } from 'lucide-react';
import { Spinner } from '@/components/ui-v2';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useAuthStore } from '@/stores/authStore';
import { LinkedAccountsSettings, DevicesManagement } from '@/features/auth';
import { IpWhitelistSettings } from '@/features/auth/components/IpWhitelistSettings';
import { CertificateManagement } from '@/features/admin/components/CertificateManagement';
import { BreachCheck } from '@/features/admin/components/BreachCheck';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

export const SecuritySettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error } = useCompanySettings();
  const { user } = useAuthStore();

  // Vérifier si l'utilisateur est admin
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.isCompanyAdmin;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
            <Shield className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{error}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-red-100 rounded-xl">
          <Shield className="w-5 h-5 text-red-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.security.title')}</h2>
      </div>

      {/* Section 1: Comptes et Appareils */}
      <div>
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          {t('settings.security.accessAndDevices')}
        </h3>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Comptes liés (SSO Google/Microsoft) */}
          <LinkedAccountsSettings />

          {/* Appareils connectés (Device Fingerprinting) */}
          <DevicesManagement />
        </div>
      </div>

      {/* Section 2: Whitelist IP */}
      <div>
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          {t('settings.security.networkRestrictions')}
        </h3>
        <div className={cardClasses}>
          <IpWhitelistSettings />
        </div>
      </div>

      {/* Section 3: Administration Sécurité (Admin only) */}
      {isAdmin && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
              {t('settings.security.advancedAdmin')}
            </h3>
            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
              Admin
            </span>
          </div>

          <div className="space-y-6">
            {/* Certificats mTLS - ISO 27001 A.9.4.2 */}
            <div className={cardClasses}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-teal-100 rounded-xl">
                  <Key className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{t('settings.security.mtlsCertificates')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.security.mtlsCertificatesDesc')}</p>
                </div>
              </div>
              <CertificateManagement />
            </div>

            {/* Détection de failles - ISO 27001 A.9.4.3 */}
            <div className={cardClasses}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 rounded-xl">
                  <ShieldAlert className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{t('settings.security.breachDetection')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.security.breachDetectionDesc')}</p>
                </div>
              </div>
              <BreachCheck />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettingsPage;
